/**
 * Multi-Event Comparison Page
 *
 * Allows users to select 2-3 events and compare their pain impact side-by-side.
 * V1-inspired: atmospheric header, premium surfaces, bold typography,
 * and refined step transitions.
 *
 * Phase 4.1: Multi-event comparison view
 */

import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, GitCompare, Info, Sparkles } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useBackClose } from "@/hooks";
import {
  EventSelectionPanel,
  MultiEventComparisonCard,
} from "@/components/events";
import { useMultiEventCorrelation } from "@/hooks/use-multi-event-correlation";
import type { EventResponse } from "@floline/api-client/types";

// --- Route Definition ---

export const Route = createFileRoute("/_authenticated/events/compare")({
  component: EventComparisonPage,
});

// --- Types ---

type ComparisonStep = "selection" | "comparison";

// --- Main Component ---

function EventComparisonPage() {
  const navigate = useNavigate();
  const { t } = useTranslation("events");
  
  // State
  const [step, setStep] = useState<ComparisonStep>("selection");
  const [selectedEventIds, setSelectedEventIds] = useState<string[]>([]);
  const [selectedEvents, setSelectedEvents] = useState<EventResponse[]>([]);

  // Mobile back-button support: comparison -> selection is one step.
  useBackClose(step === "comparison", () => setStep("selection"));
  
  // Fetch correlations when events are selected
  const { eventsWithCorrelations, isLoading } = useMultiEventCorrelation(
    step === "comparison" ? selectedEvents : []
  );
  
  // Handlers
  const handleCompare = (events: EventResponse[]) => {
    setSelectedEvents(events);
    setStep("comparison");
  };
  
  const handleBackToSelection = () => {
    setStep("selection");
  };
  
  const handleBackToTimeline = () => {
    navigate({ to: "/timeline" });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header -- V1 glass morphism with atmospheric treatment */}
      <header className="sticky top-0 z-10 border-b border-border/50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex h-14 items-center gap-3 px-4 max-w-4xl mx-auto">
          <Button
            variant="ghost"
            size="icon"
            onClick={step === "comparison" ? handleBackToSelection : handleBackToTimeline}
            aria-label={step === "comparison" ? t("compare.backToSelection") : t("compare.backToTimeline")}
            className="shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-bold tracking-tight truncate">
              {step === "selection" ? t("compare.selectionTitle") : t("compare.comparisonTitle")}
            </h1>
            <p className="text-xs text-muted-foreground truncate">
              {step === "selection" ? t("compare.selectionDescription") : t("compare.comparisonDescription")}
            </p>
          </div>
          {step === "selection" && (
            <Link to="/events/new">
              <Button variant="outline" size="sm" className="gap-1.5 text-xs font-semibold">
                <Sparkles className="h-3.5 w-3.5" />
                {t("compare.logEvent")}
              </Button>
            </Link>
          )}
        </div>
      </header>

      {/* Main Content */}
      <section className="max-w-4xl mx-auto py-6 px-4">
        {step === "selection" ? (
          <div className="space-y-6 animate-in fade-in-0 duration-200">
            {/* Info card -- V1 atmospheric hint card */}
            <Card className="relative overflow-hidden bg-primary/[0.03] border-primary/15 dark:bg-primary/[0.05] dark:border-primary/20">
              {/* Subtle teal glow */}
              <div
                className="pointer-events-none absolute -left-16 -top-16 h-40 w-40 rounded-full bg-primary/[0.08] blur-3xl dark:bg-primary/[0.12]"
                aria-hidden="true"
              />
              <CardContent className="relative z-10 flex items-center gap-4 py-6">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 dark:bg-primary/15">
                  <Info className="h-[18px] w-[18px] text-primary" />
                </div>
                <div className="text-sm text-muted-foreground space-y-1.5">
                  <p className="leading-relaxed">
                    {t("compare.infoParagraph1")}
                  </p>
                  <p className="leading-relaxed">
                    {t("compare.infoParagraph2")}
                  </p>
                </div>
              </CardContent>
            </Card>
            
            {/* Event selection panel */}
            <EventSelectionPanel
              selectedEventIds={selectedEventIds}
              onSelectionChange={setSelectedEventIds}
              onCompare={handleCompare}
            />
          </div>
        ) : (
          <div className="space-y-6 animate-in fade-in-0 slide-in-from-bottom-2 duration-300">
            {/* Comparison card */}
            <MultiEventComparisonCard
              eventsWithCorrelations={eventsWithCorrelations}
              isLoading={isLoading}
            />
            
            {/* Back button -- clear CTA for re-selection */}
            <div className="flex justify-center pt-2">
              <Button
                variant="outline"
                onClick={handleBackToSelection}
                className="gap-2 font-semibold"
              >
                <GitCompare className="h-4 w-4" />
                {t("compare.compareDifferent")}
              </Button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
